import React from 'react';
import { isCustomerFromGensler } from '@mb/lib/customer';
import { Divider } from '@mui/material';
import { CustomIcon } from 'components/CustomIcon';
import Typography from 'components/Typography/Typography';
import { useIntl } from 'react-intl';
import { DescriptionContainerSkeleton } from './DescriptionContainerSkeleton';
import { definedTranslation } from '../../i18n';
import { ProductDetails } from '../../types/product';

const isGenslerUser = isCustomerFromGensler();

interface DescriptionContainerProps {
  isLoading: boolean;
  activeProduct: ProductDetails;
  showSize?: boolean;
}

export const DescriptionContainer: React.FC<DescriptionContainerProps> = ({
  isLoading,
  activeProduct,
  showSize = true,
}) => {
  const intl = useIntl();

  if (isLoading) {
    return <DescriptionContainerSkeleton />;
  }

  const shouldShowGpsLabel =
    activeProduct?.originalMaterial?.isGpsCompliant && isGenslerUser;
  const translation = {
    infoUsageLabel: intl?.formatMessage(
      definedTranslation.miniPDP.infoUsageLabel,
    ),
    infoSkuLabel: intl?.formatMessage(definedTranslation.miniPDP.infoSkuLabel),
    indoorOutdoorLabel: intl?.formatMessage(
      definedTranslation.miniPDP.indoorOutdoorLabel,
    ),
    sizeLabel: intl?.formatMessage(definedTranslation.miniPDP.sizeLabel),
    sampleNoteLabel: intl?.formatMessage(definedTranslation.miniPDP.noteLabel),
  };

  return (
    <>
      {shouldShowGpsLabel && (
        <div className="flex flex-col">
          <span
            className="flex items-center gap-1 text-xs"
            style={{ color: '#e82011' }}
          >
            <span>GPS</span>
            <CustomIcon name="check" dense />
          </span>
          <Divider variant="fullWidth" orientation="horizontal" />
        </div>
      )}
      <div className="grid grid-cols-3 gap-4">
        <DescriptionSection
          label={translation.infoUsageLabel}
          value={activeProduct.categoryLabel}
        />
        <div className="flex gap-3">
          <Divider variant="fullWidth" orientation="vertical" />
          <DescriptionSection
            label={translation.indoorOutdoorLabel}
            value={activeProduct.indoor}
          />
        </div>
        <div className="flex gap-3">
          <Divider variant="fullWidth" orientation="vertical" />
          <DescriptionSection
            label={translation.infoSkuLabel}
            value={activeProduct.sku}
          />
        </div>
      </div>
      {showSize && Boolean(activeProduct.sizeNote) && (
        <DescriptionSection
          label={translation.sizeLabel}
          value={activeProduct.sizeNote ?? ''}
        />
      )}
      {Boolean(activeProduct?.sampleNote) && (
        <DescriptionSection
          label={translation.sampleNoteLabel}
          value={activeProduct.sampleNote ?? ''}
        />
      )}
    </>
  );
};

type DescriptionSectionProps = {
  label: string;
  value: string;
};

function DescriptionSection({ label, value }: DescriptionSectionProps) {
  return (
    <div className="flex flex-col">
      <Typography component="span" variant="body" color="grey" fontSize="13px">
        {label}
      </Typography>
      <Typography component="p" variant="body" color="primary">
        {value}
      </Typography>
    </div>
  );
}
